"use client";

import ChevronDownIcon from "../icons/ChevronDownIcon";
import CheckIcon from "../icons/CheckIcon";
import BuildingIcon from "../icons/BuildingIcon";
import Button from "../ui/Button";
import type { Store } from "@/lib/types/stores";
import { getStoreStatus } from "@/lib/store-hours";

interface StoreSelectorProps {
  stores: Store[];
  selectedLocationId: number | null;
  mode: "pickup" | "delivery";
  resolvedStore: Store | null;
  postalCode: string;
  /** True while store change is applying (soft route refresh). */
  isPending?: boolean;
  showLocationsDropdown: boolean;
  onToggleDropdown: () => void;
  onLocationSelect: (locationId: number) => void;
  onShowLocationModal: () => void;
}

export default function StoreSelector({
  stores,
  selectedLocationId,
  mode,
  resolvedStore,
  postalCode,
  isPending = false,
  showLocationsDropdown,
  onToggleDropdown,
  onLocationSelect,
  onShowLocationModal,
}: StoreSelectorProps) {
  const selectedStore = stores.find(
    (s) => s.covaLocationId === selectedLocationId
  );

  const pendingClass = isPending ? "opacity-60 pointer-events-none" : "";

  // Live open/closed status from the store's real hours (Mountain Time).
  // Prefer the mode-appropriate store, but fall back to the other so the badge
  // keeps showing a store's hours, e.g. when the delivery modal is opened
  // (which clears resolvedStore) or cancelled back to pickup without picking a
  // store, the badge stays put instead of disappearing.
  const statusStore =
    (mode === "delivery" ? resolvedStore : selectedStore) ??
    selectedStore ??
    resolvedStore ??
    null;
  // Delivery mode reflects the store's DELIVERY hours; pickup reflects the
  // in-store hours, e.g. delivery can open at 10 AM even if the store opens
  // at 9 AM, so at 9:53 AM the delivery badge must read "Closed · Opens 10 AM".
  const status =
    mode === "delivery"
      ? getStoreStatus(statusStore?.deliveryOpenTime, statusStore?.deliveryCloseTime)
      : getStoreStatus(statusStore?.openTime, statusStore?.closeTime);

  return (
    <div
      className={`flex items-center gap-2 transition-opacity duration-150 ${pendingClass}`}
      aria-busy={isPending}
    >
      {isPending ? (
        <span className="sr-only">Updating store location</span>
      ) : null}
      <BuildingIcon size={24} className="w-6 h-6 text-neutral-70" />
      <div className="hidden md:flex flex-col items-start">
        {status ? (
          <div className="flex items-center gap-2 text-sm">
            <span className={`font-medium ${status.open ? "text-green-50" : "text-neutral-70"}`}>
              {status.open ? "Open" : "Closed"}
            </span>
            <span className="text-neutral-70">- {status.label}</span>
          </div>
        ) : null}
        <div className="relative">
          {mode === "pickup" ? (
            <>
              {/* TODO(button-migration): selector label + trailing chevron with dynamic text. */}
              <Button raw
                type="button"
                onClick={onToggleDropdown}
                disabled={isPending}
                className="flex flex-row items-center gap-1 text-sm text-neutral-70 hover:text-blue-80 transition-colors disabled:cursor-wait"
              >
                {/* No store resolved means the store list has not loaded, not
                    that Ogden is selected. Naming a store here hid that: the
                    hours badge above vanishes with the data while the label
                    kept confidently reading "Calgary: Ogden". */}
                {selectedStore
                  ? `${selectedStore.city || "Calgary"}: ${selectedStore.name}`
                  : "Choose store"}
                <ChevronDownIcon size={12} className="w-3 h-3 flex-shrink-0" />
              </Button>
              {showLocationsDropdown && (
                <div className="absolute right-0 mt-1 w-max rounded-lg border border-neutral-10 shadow-lg z-50 max-h-60 overflow-y-auto">
                  {stores.map((store) => (
                    // TODO(button-migration): dropdown option with text + optional check icon, complex layout.
                    <Button raw
                      key={store.covaLocationId}
                      type="button"
                      onClick={() => {
                        onLocationSelect(store.covaLocationId);
                        onToggleDropdown();
                      }}
                      className={`flex flex-row items-center justify-between w-full px-4 py-2 text-left text-sm bg-bg-0 hover:bg-bg-2 transition-colors ${
                        selectedLocationId === store.covaLocationId
                          ? "bg-bg-2 font-medium"
                          : ""
                      }`}
                    >
                      <span>
                        {store.city || "Calgary"}: {store.name}
                      </span>
                      {selectedLocationId === store.covaLocationId && (
                        <CheckIcon size={16} className="ml-2 w-4 h-4 flex-shrink-0" />
                      )}
                    </Button>
                  ))}
                </div>
              )}
            </>
          ) : (
            <Button raw
              type="button"
              onClick={onShowLocationModal}
              disabled={isPending}
              className="flex flex-row items-center gap-1 text-sm text-neutral-70 hover:text-blue-80 transition-colors disabled:cursor-wait"
            >
              {resolvedStore
                ? `${resolvedStore.city || "Calgary"}: ${resolvedStore.name}`
                : postalCode || "Enter postal code"}
              <ChevronDownIcon size={12} className="w-3 h-3 flex-shrink-0" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
